// ===================================================================
// components/TisserandGame.tsx
// "Le tisserand" — tisser des phrases. Chaque bobine porte un mot ; on
// choisit les bobines dans l'ordre de la phrase et chaque mot devient une
// rangée du pagne. Quelques bobines intruses, trois cœurs, un indice et
// "Défaire" pour retirer le dernier fil.
//
// Maquette Claude Design "Jogolinga - Tisserand" (qui tissait un mot lettre
// par lettre) : ici un fil = un mot, pour apprendre l'ordre des phrases.
// ===================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { getLanguageData } from '../data/languages';
import { getSentenceTranslation } from '../utils/localizedContent';
import { recordActivity } from '../hooks/useDailyStreak';
import { LanguageCode, Sentence } from '../types/types';
import GameStage from './common/GameStage';
import './TisserandGame.css';

interface TisserandGameProps {
  languageCode: LanguageCode;
  onBack: () => void;
}

// ── Constantes ────────────────────────────────────────────────────
const SENTENCES_PER_PIECE = 6;
const MIN_WORDS = 2;
const MAX_WORDS = 6; // rangées qui tiennent sur le métier
const HEARTS = 3;
const XP_PER_SENTENCE = 10;
const XP_PER_HEART_LEFT = 5;
const CLOTH_W = 262;
const CLOTH_H = 316;

const LANG_NAMES: Partial<Record<LanguageCode, string>> = {
  wf: 'Wolof', ba: 'Bambara', la: 'Lingala', ff: 'Peul', co: 'Copte', sw: 'Swahili', yo: 'Yoruba',
};

// Fils : [couleur, encre]
const THREADS: [string, string][] = [
  ['#C67B4E', '#FFF8EC'], ['#A4553A', '#FFF8EC'], ['#8A6D3B', '#FFF8EC'], ['#B8862F', '#2A1A12'],
  ['#5E6B3A', '#FFF8EC'], ['#2A1A12', '#E8C9A0'], ['#D9A441', '#2A1A12'],
];
const SERIF = "'Young Serif', 'Noto Sans Coptic', Georgia, serif";

// ── Utilitaires ───────────────────────────────────────────────────
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
const abs = (s: React.CSSProperties): React.CSSProperties => ({ position: 'absolute', ...s });

function awardXp(languageCode: LanguageCode, xpGained: number) {
  if (xpGained <= 0) return;
  try {
    const xpKey = `${languageCode}-totalXP`;
    const newTotal = parseInt(localStorage.getItem(xpKey) || '0', 10) + xpGained;
    localStorage.setItem(xpKey, String(newTotal));
    window.dispatchEvent(new CustomEvent('xpUpdated', { detail: { newTotal, gained: xpGained, source: 'weaver' } }));
  } catch { /* ignore */ }
  recordActivity();
}

interface Piece {
  sentence: Sentence;
  meaning: string;
}

/** 6 phrases d'un même thème (ou mélangées si aucun thème n'en a assez). */
function makePieces(languageCode: LanguageCode, english: boolean): Piece[] {
  if (languageCode === 'ka') return [];
  const all = (getLanguageData(languageCode).sentencesToConstruct ?? [])
    .filter(s => Array.isArray(s.words) && s.words.length >= MIN_WORDS && s.words.length <= MAX_WORDS);
  const byCat = new Map<string, Sentence[]>();
  all.forEach(s => {
    const c = s.category ?? '';
    byCat.set(c, [...(byCat.get(c) ?? []), s]);
  });
  const themes = Array.from(byCat.values()).filter(list => list.length >= SENTENCES_PER_PIECE);
  const source = themes.length ? themes[Math.floor(Math.random() * themes.length)] : all;
  return shuffle(source).slice(0, SENTENCES_PER_PIECE).map(sentence => ({
    sentence,
    meaning: getSentenceTranslation(sentence, english ? 'en' : 'fr'),
  }));
}

/** Mots des autres phrases de la langue, pour les bobines intruses. */
function wordPool(languageCode: LanguageCode): string[] {
  if (languageCode === 'ka') return [];
  const set = new Set<string>();
  (getLanguageData(languageCode).sentencesToConstruct ?? []).forEach(s => (s.words ?? []).forEach(w => {
    if (w && /\p{L}/u.test(w)) set.add(w);
  }));
  return Array.from(set);
}

// ── Icônes ────────────────────────────────────────────────────────
const HeartIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#A4553A" aria-hidden="true"><path d="M12 21C5 15.5 2 12.3 2 8.5A4.8 4.8 0 0 1 12 6.2A4.8 4.8 0 0 1 22 8.5C22 12.3 19 15.5 12 21Z" /></svg>
);

// ===================================================================
// Le métier à tisser
// ===================================================================
interface Spool { id: number; word: string; color: string; ink: string; used: boolean }
interface Row { id: number; word: string; color: string; ink: string }
type Status = 'play' | 'weaving' | 'win' | 'over' | 'done';

interface LoomProps {
  pieces: Piece[];
  pool: string[];
  langName: string;
  t: TFunction;
  onExit: () => void;
  onNewPiece: () => void;
  onXp: (xp: number) => void;
}
interface LoomState {
  idx: number;
  spools: Spool[];
  rows: Row[];
  hearts: number;
  status: Status;
  weave: number;
  bad: number | null;
  lastXp: number;
}

let UID = 0;

class Loom extends React.Component<LoomProps, LoomState> {
  state: LoomState = { idx: 0, spools: [], rows: [], hearts: HEARTS, status: 'play', weave: 0, bad: null, lastXp: 0 };
  timers: ReturnType<typeof setTimeout>[] = [];
  woven = 0;    // phrases tissées dans cette partie
  awarded = 0;  // phrases déjà payées en XP

  later(fn: () => void, ms: number) { this.timers.push(setTimeout(fn, ms)); }
  componentDidMount() { this.setup(0); }
  componentWillUnmount() {
    this.timers.forEach(clearTimeout);
    this.pay(); // partie quittée en cours : les phrases tissées comptent
  }

  pay(bonus = 0): number {
    const xp = (this.woven - this.awarded) * XP_PER_SENTENCE + bonus;
    this.awarded = this.woven;
    if (xp > 0) this.props.onXp(xp);
    return xp;
  }

  setup(idx: number) {
    const words = this.props.pieces[idx].sentence.words;
    const lower = new Set(words.map(w => w.toLocaleLowerCase()));
    const candidates = shuffle(this.props.pool.filter(w => !lower.has(w.toLocaleLowerCase())));
    const decoys = candidates.slice(0, words.length <= 4 ? 3 : 2);
    const cols = shuffle(THREADS);
    const spools = shuffle([...words, ...decoys]).map((word, i) => ({
      id: ++UID, word, color: cols[i % cols.length][0], ink: cols[i % cols.length][1], used: false,
    }));
    this.setState({ idx, rows: [], status: 'play', bad: null, spools });
  }

  tap(id: number) {
    const { status, spools, rows, idx, hearts } = this.state;
    const s = spools.find(x => x.id === id);
    if (status !== 'play' || !s || s.used) return;
    const words = this.props.pieces[idx].sentence.words;
    const need = words[rows.length];
    if (s.word !== need) {
      const left = hearts - 1;
      this.setState({ bad: id, hearts: left });
      this.later(() => {
        if (left <= 0) this.pay();
        this.setState({ bad: null, status: left <= 0 ? 'over' : 'play' });
      }, 500);
      return;
    }
    const next = [...rows, { id: s.id, word: s.word, color: s.color, ink: s.ink }];
    this.setState(st => ({ rows: next, weave: st.weave + 1, spools: st.spools.map(x => (x.id === id ? { ...x, used: true } : x)) }));
    if (next.length === words.length) {
      this.woven += 1;
      this.setState({ status: 'weaving' });
      this.later(() => this.setState({ status: 'win' }), 900);
    }
  }

  undo = () => {
    const { rows, status } = this.state;
    if (status !== 'play' || !rows.length) return;
    const last = rows[rows.length - 1];
    this.setState(st => ({ rows: st.rows.slice(0, -1), spools: st.spools.map(x => (x.id === last.id ? { ...x, used: false } : x)) }));
  };

  hint = () => {
    const { status, spools, rows, idx } = this.state;
    if (status !== 'play') return;
    const need = this.props.pieces[idx].sentence.words[rows.length];
    const s = spools.find(x => !x.used && x.word === need);
    if (s) this.tap(s.id);
  };

  next = () => {
    const { idx, hearts } = this.state;
    if (idx + 1 >= this.props.pieces.length) {
      const xp = this.pay(hearts * XP_PER_HEART_LEFT);
      this.setState({ status: 'done', lastXp: xp });
    } else {
      this.setup(idx + 1);
    }
  };

  /** "Renouer le fil" : trois cœurs, on reprend la phrase ratée. */
  retie = () => {
    this.setState({ hearts: HEARTS });
    this.setup(this.state.idx);
  };

  renderSheet() {
    const { status, idx, hearts, lastXp } = this.state;
    const { t, pieces } = this.props;
    const p = pieces[idx];
    const sentenceText = p.sentence.words.join(' ').replace(/\s+([?!.,;:])/g, '$1');
    const sheets: Partial<Record<Status, { title: string; color: string; text: string; cta: string; action: () => void; secondary?: boolean }>> = {
      win: {
        title: t('weaver.win.title', 'Bien tissé !'), color: '#2A1A12',
        text: t('weaver.win.text', '« {{sentence}} » veut dire « {{meaning}} ».', { sentence: sentenceText, meaning: p.meaning }),
        cta: idx + 1 >= pieces.length ? t('weaver.win.finish', 'Terminer') : t('weaver.win.next', 'Phrase suivante'),
        action: this.next,
      },
      done: {
        title: t('weaver.done.title', 'Ton pagne est terminé'), color: '#2A1A12',
        text: t('weaver.done.text', '{{count}} phrases tissées avec {{hearts}} cœur(s) restant(s).', { count: pieces.length, hearts })
          + (lastXp > 0 ? ` +${lastXp} XP` : ''),
        cta: t('weaver.done.cta', 'Nouveau pagne'), action: this.props.onNewPiece, secondary: true,
      },
      over: {
        title: t('weaver.over.title', 'Le fil a cassé'), color: '#A4553A',
        text: t('weaver.over.text', 'La phrase était « {{sentence}} ». Renoue le fil pour réessayer.', { sentence: sentenceText }),
        cta: t('weaver.over.cta', 'Renouer le fil'), action: this.retie, secondary: true,
      },
    };
    const sh = sheets[status];
    if (!sh) return null;
    return (
      <div className="tw-sheet" role="dialog" aria-live="polite">
        <span style={{ fontFamily: SERIF, fontSize: 28, color: sh.color }}>{sh.title}</span>
        <span style={{ fontSize: 16, lineHeight: 1.45, color: '#4A3424' }}>{sh.text}</span>
        <button type="button" className="tw-primary" onClick={sh.action} style={{ marginTop: 8 }}>{sh.cta}</button>
        {sh.secondary && (
          <button type="button" className="tw-link" onClick={this.props.onExit}>{t('weaver.backToPractice', 'Retour à Pratiquer')}</button>
        )}
      </div>
    );
  }

  render() {
    const { idx, spools, rows, hearts, status, weave, bad } = this.state;
    const { pieces, t, langName, onExit } = this.props;
    const p = pieces[idx];
    const len = p.sentence.words.length;
    const rowH = Math.min(56, Math.floor(CLOTH_H / len));
    const progress = ((idx + (status === 'win' || status === 'done' ? 1 : 0)) / pieces.length) * 100;
    const meaningSize = p.meaning.length <= 16 ? 32 : p.meaning.length <= 28 ? 26 : p.meaning.length <= 44 ? 21 : 18;
    const lastRow = rows[rows.length - 1];
    const rowTop = CLOTH_H - rows.length * rowH + (rowH - 20) / 2;

    return (
      <div className="tw-screen">
        {/* En-tête : fermer, progression, cœurs */}
        <div style={abs({ left: 16, right: 16, top: 22, display: 'flex', alignItems: 'center', gap: 14 })}>
          <button type="button" className="tw-icon-btn" onClick={onExit} aria-label={t('weaver.close', 'Fermer')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A1A12" strokeWidth="2.2" strokeLinecap="round"><path d="M5 5L19 19M19 5L5 19" /></svg>
          </button>
          <div style={{ flex: 1, height: 12, borderRadius: 6, background: '#D4BE94', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 6, background: '#C67B4E', transition: 'width .6s ease', width: `${progress}%` }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: 17, color: '#A4553A' }} aria-label={t('weaver.heartsLeft', '{{count}} cœur(s)', { count: hearts })}>
            <HeartIcon /><span>{hearts}</span>
          </div>
        </div>
        <div style={abs({ left: 24, right: 24, top: 88, display: 'flex', alignItems: 'center', gap: 12, fontWeight: 800, fontSize: 12, letterSpacing: '.2em', color: '#C67B4E' })}>
          <span style={{ flex: 1, height: 1, background: '#D9B892' }} />
          <span style={{ whiteSpace: 'nowrap' }}>◆ {t('weaver.eyebrow', 'L’ATELIER DU TISSERAND')} ◆</span>
          <span style={{ flex: 1, height: 1, background: '#D9B892' }} />
        </div>
        <div style={abs({ left: 20, right: 20, top: 112, height: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, textAlign: 'center' })}>
          <span style={{ fontSize: 15, color: '#4A3424' }}>{t('weaver.prompt', 'Tisse cette phrase en {{langLower}}, fil après fil', { lang: langName, langLower: langName.toLocaleLowerCase() })}</span>
          <span style={{ fontFamily: SERIF, fontSize: meaningSize, lineHeight: 1.15 }}>« {p.meaning} »</span>
        </div>

        {/* Métier à tisser */}
        <div style={abs({ left: 34, top: 200, width: 322, height: 372 })}>
          <div className="tw-post" style={{ left: 0 }} />
          <div className="tw-post" style={{ right: 0 }} />
          <div className="tw-beam" style={{ top: 8 }} />
          <div className="tw-beam" style={{ bottom: 8 }} />
          <div style={abs({ left: 30, right: 30, top: 28, bottom: 28, background: '#F0E3C6', overflow: 'hidden' })}>
            <svg width={CLOTH_W} height={CLOTH_H} viewBox={`0 0 ${CLOTH_W} ${CLOTH_H}`} style={abs({ inset: 0 })} aria-hidden="true">
              <defs><pattern id="twWarp" width="9" height={CLOTH_H} patternUnits="userSpaceOnUse"><path d={`M4.5 0V${CLOTH_H}`} stroke="#C9AD7A" strokeWidth="1.6" /></pattern></defs>
              <rect width={CLOTH_W} height={CLOTH_H} fill="url(#twWarp)" />
            </svg>
            <div style={abs({ left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column-reverse', animation: status === 'win' || status === 'weaving' ? 'twGlow 1.2s ease-in-out' : 'none' })}>
              {rows.map((r, i) => (
                <div key={r.id} className="tw-row" style={{ height: rowH, background: r.color }}>
                  <svg width={CLOTH_W} height={rowH} viewBox={`0 0 ${CLOTH_W} ${rowH}`} style={abs({ inset: 0 })} aria-hidden="true">
                    <defs>
                      <pattern id={`twWeft${i}`} width="9" height="6" patternUnits="userSpaceOnUse"><path d="M0 3H9" stroke="rgba(0,0,0,.14)" strokeWidth="1" /></pattern>
                      <pattern id={`twZig${i}`} width="14" height="8" patternUnits="userSpaceOnUse"><path d="M0 7L7 1L14 7" fill="none" stroke={r.ink} strokeWidth="1.8" /></pattern>
                    </defs>
                    <rect width={CLOTH_W} height={rowH} fill={`url(#twWeft${i})`} />
                    <rect y="2" width={CLOTH_W} height="8" fill={`url(#twZig${i})`} />
                    <rect y={rowH - 10} width={CLOTH_W} height="8" fill={`url(#twZig${i})`} />
                  </svg>
                  <span style={{ position: 'relative', fontFamily: SERIF, fontSize: r.word.length > 9 ? 20 : 26, lineHeight: 1, color: r.ink }}>{r.word}</span>
                </div>
              ))}
            </div>
            {/* La navette traverse à chaque fil posé */}
            {weave > 0 && lastRow && status !== 'win' && (
              <div key={`sh${weave}`} style={abs({ left: 0, top: rowTop, width: 64, height: 20, animation: 'twShuttle .6s ease-in-out forwards', pointerEvents: 'none' })}>
                <svg width="64" height="20" viewBox="0 0 64 20" aria-hidden="true">
                  <path d="M2 10Q14 1 32 2Q50 1 62 10Q50 19 32 18Q14 19 2 10Z" fill="#8A5A34" stroke="#2A1A12" strokeWidth="1.5" />
                  <rect x="22" y="7" width="20" height="6" rx="3" fill={lastRow.color} />
                </svg>
              </div>
            )}
          </div>
        </div>

        <span style={abs({ left: 0, right: 0, top: 580, textAlign: 'center', fontSize: 13, color: '#8A7358' })}>
          {status === 'play'
            ? t('weaver.hintLine', 'Fil {{n}} sur {{total}} · choisis la bobine suivante', { n: Math.min(rows.length + 1, len), total: len })
            : ' '}
        </span>

        {/* Bobines */}
        <div style={abs({ left: 14, right: 14, top: 600, height: 160, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', justifyContent: 'center', gap: 8, paddingTop: 2 })}>
          {spools.map(s => (
            <button key={s.id} type="button" className="tw-spool" onClick={() => this.tap(s.id)} disabled={s.used || status !== 'play'}
              aria-label={s.word}
              style={{ opacity: s.used ? 0.25 : 1, transform: s.used ? 'translateY(8px) scale(.9)' : 'none', animation: bad === s.id ? 'twShake .45s ease-in-out' : 'none' }}>
              <span className="tw-spool__bar" />
              <span className="tw-spool__body" style={{ background: s.color }}>
                <span className="tw-spool__label" style={{ fontSize: s.word.length > 9 ? 13 : s.word.length > 6 ? 15 : 17 }}>{s.word}</span>
              </span>
              <span className="tw-spool__bar" />
            </button>
          ))}
        </div>

        {/* Indice / Défaire */}
        <div style={abs({ left: 24, right: 24, bottom: 24, display: 'flex', gap: 12 })}>
          <button type="button" className="tw-secondary" onClick={this.hint}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C67B4E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18H15M10 21H14M12 3A6 6 0 0 0 8.5 13.9C9.3 14.5 9.5 15.2 9.5 16H14.5C14.5 15.2 14.7 14.5 15.5 13.9A6 6 0 0 0 12 3Z" /></svg>
            {t('weaver.hint', 'Indice')}
          </button>
          <button type="button" className="tw-secondary" onClick={this.undo}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C67B4E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 14L4 9L9 4M4 9H15A5 5 0 0 1 15 19H11" /></svg>
            {t('weaver.undo', 'Défaire')}
          </button>
        </div>

        {this.renderSheet()}
      </div>
    );
  }
}

// ===================================================================
// Composant principal
// ===================================================================
const TisserandGame: React.FC<TisserandGameProps> = ({ languageCode, onBack }) => {
  const { t, i18n } = useTranslation();
  const english = !!i18n.language?.startsWith('en');
  const langName = LANG_NAMES[languageCode] ?? languageCode.toUpperCase();
  const pool = useMemo(() => wordPool(languageCode), [languageCode]);

  const [pieces, setPieces] = useState<Piece[]>(() => makePieces(languageCode, english));
  const [gameKey, setGameKey] = useState(0);
  useEffect(() => { setPieces(makePieces(languageCode, english)); setGameKey(k => k + 1); }, [languageCode, english]);

  const newPiece = () => { setPieces(makePieces(languageCode, english)); setGameKey(k => k + 1); };
  const onXp = useCallback((xp: number) => awardXp(languageCode, xp), [languageCode]);

  return (
    <GameStage className="tw-root">
      {pieces.length === 0 ? (
        <div className="tw-screen">
          <button type="button" className="tw-icon-btn" onClick={onBack} aria-label={t('weaver.close', 'Fermer')} style={abs({ left: 16, top: 22 })}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2A1A12" strokeWidth="2.2" strokeLinecap="round"><path d="M5 5L19 19M19 5L5 19" /></svg>
          </button>
          <div style={abs({ left: 32, right: 32, top: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' })}>
            <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.2em', color: '#C67B4E' }}>◆ {t('weaver.eyebrow', 'L’ATELIER DU TISSERAND')} ◆</span>
            <span style={{ fontFamily: SERIF, fontSize: 28 }}>{t('weaver.comingSoon', 'Bientôt disponible')}</span>
            <span style={{ fontSize: 16, lineHeight: 1.5, color: '#4A3424' }}>{t('weaver.comingSoonText', 'Le tisserand arrive prochainement pour cette langue.')}</span>
          </div>
        </div>
      ) : (
        <Loom key={gameKey} pieces={pieces} pool={pool} langName={langName} t={t} onExit={onBack} onNewPiece={newPiece} onXp={onXp} />
      )}
    </GameStage>
  );
};

export default TisserandGame;
